// src/pages/onboarding/ai-result.tsx
import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { useOnboardingData } from "@/contexts/onboarding-data-context";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Loader2,
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  Copy,
  Eye,
  Bug,
  RotateCcw,
  BarChart2,
  ChevronsRight,
} from "lucide-react";
import { LLMResponse } from "@/lib/types/models";
import PromptResponseDisplay from "@/components/prompt-detail/PromptResponseDisplay";
import { useBackendApi } from "@/hooks/use-backend-api";
import { DEV_FALLBACK_WEB_APP_URL } from "@/lib/onboarding-data-utils";

// Importing the raw prompt strings.
import searchTermNgramAnalysisPrompt from "@/data/prompt-templates/search-term-ngram-analysis.js";
import keywordNgramAnalysisPrompt from "@/data/prompt-templates/keyword-ngram-analysis.js";
import searchTermPatternAnalysisPrompt from "@/data/prompt-templates/search-term-pattern-analysis.js";
import keywordExpansionOpportunitiesPrompt from "@/data/prompt-templates/keyword-expansion-opportunities.js";
import { ONBOARDING_SHEET_TABS } from "@/lib/onboarding-data-utils";

const DEBUG = process.env.NODE_ENV === "development";
const MAX_ROWS_FOR_ONBOARDING = 100;

const insightConfig = {
  "keyword-ngram": {
    title: "Keyword N-gram Analysis",
    dataSourceKey: ONBOARDING_SHEET_TABS.KEYWORD80,
    promptTemplate: keywordNgramAnalysisPrompt,
    insightID: "keyword-ngram",
  },
  "search-term-patterns": {
    title: "Search Term Pattern Analysis",
    dataSourceKey: ONBOARDING_SHEET_TABS.SEARCH80,
    promptTemplate: searchTermPatternAnalysisPrompt,
    insightID: "search-term-patterns",
  },
  "search-term-ngram": {
    title: "Search Term N-gram Analysis",
    dataSourceKey: ONBOARDING_SHEET_TABS.SEARCH80,
    promptTemplate: searchTermNgramAnalysisPrompt,
    insightID: "search-term-ngram",
  },
  "keyword-expansion": {
    title: "Keyword Expansion Opportunities",
    dataSourceKey: ONBOARDING_SHEET_TABS.KEYWORD80,
    promptTemplate: keywordExpansionOpportunitiesPrompt,
    insightID: "keyword-expansion",
  },
};

type InsightId = keyof typeof insightConfig;

const OnboardingAiResultPage = () => {
  const { insightId } = useParams<{ insightId: string }>();
  const { user, isSignedIn, isLoaded } = useUser();
  const {
    data: onboardingData,
    loadingStatus: onboardingDataLoadingStatus,
    fetchOnboardingData,
    hasDataBeenFetched,
  } = useOnboardingData();
  const navigate = useNavigate();

  const [isExecuting, setIsExecuting] = useState(true);
  const [executionResult, setExecutionResult] = useState<LLMResponse | null>(
    null,
  );
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [editablePrompt, setEditablePrompt] = useState<string>("");
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);

  // Use backend API with Gemini Flash model
  const { callAI } = useBackendApi("gemini-3-flash-preview");

  const config = useMemo(() => {
    console.log(
      "🔧 [DEBUG] Config memo - insightId from URL params:",
      insightId,
    );
    console.log(
      "🔧 [DEBUG] Config memo - Available insightConfig keys:",
      Object.keys(insightConfig),
    );

    if (insightId && insightId in insightConfig) {
      const selectedConfig = insightConfig[insightId as InsightId];
      console.log("✅ [DEBUG] Config memo - Selected config:", selectedConfig);
      return insightConfig[insightId as InsightId];
    }
    console.log(
      "❌ [DEBUG] Config memo - No matching config found for insightId:",
      insightId,
    );
    return null;
  }, [insightId]);

  // Use ref to prevent infinite re-execution
  const hasExecutedRef = useRef(false);
  const lastConfigRef = useRef<typeof config>(null);
  const isCurrentlyExecutingRef = useRef(false);

  // Cache key for storing results in sessionStorage
  const getCacheKey = (insightId: string, accountId: string) =>
    `onboarding_ai_result_${insightId}_${accountId}`;

  // Get debug mode from localStorage
  useEffect(() => {
    const debugMode = localStorage.getItem("onboarding-debug-mode") === "true";
    setIsDebugMode(debugMode);
  }, []);

  // Load cached result on component mount
  useEffect(() => {
    if (
      !config ||
      !config.insightID ||
      !onboardingData?.account?.[0]?.accountCID
    )
      return;

    const cacheKey = getCacheKey(
      config.insightID,
      onboardingData.account[0].accountCID,
    );
    const cachedResult = sessionStorage.getItem(cacheKey);

    if (cachedResult) {
      try {
        const parsed = JSON.parse(cachedResult);
        console.log("🔄 [CACHE] Loading cached result for", config.insightID);
        setExecutionResult(parsed.result);
        setEditablePrompt(parsed.prompt);
        setIsExecuting(false);
        hasExecutedRef.current = true;
        lastConfigRef.current = config;
        console.log("✅ [CACHE] Cached result loaded successfully");
      } catch (error) {
        console.warn(
          "⚠️ [CACHE] Failed to parse cached result, will execute fresh",
          error,
        );
        sessionStorage.removeItem(cacheKey);
      }
    }
  }, [config, onboardingData]);

  // No API key checking needed - using backend API

  useEffect(() => {
    console.log(
      "🚀 [DEBUG] ======= MAIN EXECUTION useEffect TRIGGERED =======",
    );
    console.log("🚀 [DEBUG] - config exists:", !!config);
    console.log("🚀 [DEBUG] - config title:", config?.title);
    console.log(
      "🚀 [DEBUG] - onboardingDataLoadingStatus:",
      onboardingDataLoadingStatus,
    );
    console.log("🚀 [DEBUG] - onboardingData exists:", !!onboardingData);
    console.log("🚀 [DEBUG] - isLoaded:", isLoaded);
    console.log("🚀 [DEBUG] - isSignedIn:", isSignedIn);
    console.log("🚀 [DEBUG] - hasExecutedRef.current:", hasExecutedRef.current);
    console.log(
      "🚀 [DEBUG] - lastConfigRef.current === config:",
      lastConfigRef.current === config,
    );
    console.log(
      "🚀 [DEBUG] - isCurrentlyExecutingRef.current:",
      isCurrentlyExecutingRef.current,
    );

    if (!config) {
      console.log("❌ [DEBUG] Early return - no config");
      return;
    }

    // Reset execution tracking when config changes
    if (lastConfigRef.current !== config) {
      hasExecutedRef.current = false;
      lastConfigRef.current = null;
      isCurrentlyExecutingRef.current = false;
    }

    if (!isLoaded) {
      console.log("❌ [DEBUG] Early return - Clerk not loaded yet");
      return;
    }

    if (onboardingDataLoadingStatus !== "success" && !hasDataBeenFetched) {
      console.log(
        "❌ [DEBUG] Early return - onboarding data not ready, status:",
        onboardingDataLoadingStatus,
        "hasDataBeenFetched:",
        hasDataBeenFetched,
      );
      return;
    }

    if (!onboardingData) {
      console.log("❌ [DEBUG] Early return - no onboarding data");
      return;
    }

    // Using backend API - no API key validation needed

    // 🔍 CHECK FOR SAMPLE DATA AND FORCE REAL DATA FETCH
    const accountInfo = onboardingData?.account?.[0];
    console.log("🔍 [DEBUG] Checking for sample data usage");
    console.log("🔍 [DEBUG] - dexieId:", onboardingData.dexieId);
    console.log("🔍 [DEBUG] - accountInfo:", accountInfo);

    // Detect if we're using sample data (DexieID starting with 111)
    const isSampleData =
      onboardingData.dexieId?.startsWith?.("111") ||
      accountInfo?.accountName?.toLowerCase?.()?.includes?.("sample") ||
      accountInfo?.ocid?.startsWith?.("111");

    if (isSampleData && !hasDataBeenFetched) {
      console.log(
        "🚨 [DEBUG] SAMPLE DATA DETECTED! Forcing fetch from real URL",
      );
      console.log("🚨 [DEBUG] - Sample dexieId:", onboardingData.dexieId);
      console.log("🚨 [DEBUG] - Sample accountInfo:", accountInfo);
      console.log(
        "🚨 [DEBUG] - Forcing refetch from fallback URL:",
        DEV_FALLBACK_WEB_APP_URL,
      );

      // Force refetch from the fallback URL only if we haven't fetched real data
      fetchOnboardingData(DEV_FALLBACK_WEB_APP_URL);
      return;
    }

    console.log("✅ [DEBUG] Using real data (not sample data)");

    // Check if we have a cached result first
    const accountInfo2 = onboardingData?.account?.[0];
    if (accountInfo2?.accountCID && config.insightID) {
      const cacheKey = getCacheKey(config.insightID, accountInfo2.accountCID);
      const cachedResult = sessionStorage.getItem(cacheKey);
      if (cachedResult) {
        console.log("⏭️ [DEBUG] Early return - cached result exists");
        return;
      }
    }

    // Prevent infinite re-execution for the same config
    if (hasExecutedRef.current && lastConfigRef.current === config) {
      console.log("⏭️ [DEBUG] Early return - already executed for this config");
      return;
    }

    // Prevent overlapping executions
    if (isCurrentlyExecutingRef.current) {
      console.log("⏭️ [DEBUG] Early return - execution already in progress");
      return;
    }

    console.log(
      "✅ [DEBUG] All checks passed, proceeding with execution setup",
    );

    const data =
      onboardingData[config.dataSourceKey as keyof typeof onboardingData];
    console.log("📊 [DEBUG] Data source key:", config.dataSourceKey);
    console.log(
      "📊 [DEBUG] Data found:",
      data ? `Array with ${(data as any[]).length} items` : "null/undefined",
    );
    console.log("📊 [DEBUG] Data type:", typeof data);
    console.log("📊 [DEBUG] Is array:", Array.isArray(data));

    // Log the first two rows of the actual data source being used
    if (data && Array.isArray(data) && data.length > 0) {
      console.log(
        `📊 [DEBUG] First two rows from ${config.dataSourceKey}:`,
        data.slice(0, 2),
      );
    } else {
      console.log(
        "❌ [DEBUG] Data validation will fail - data is not an array or is empty",
      );
    }

    if (!data || !Array.isArray(data)) {
      console.log("❌ [DEBUG] Data validation failed - setting error");
      setExecutionError(
        `Could not find the required data source: ${config.dataSourceKey}`,
      );
      setIsExecuting(false);
      return;
    }

    if (data.length === 0) {
      console.log("❌ [DEBUG] Data array is empty");
      setExecutionError(
        `The data source ${config.dataSourceKey} is empty. Please ensure your sheet has data.`,
      );
      setIsExecuting(false);
      return;
    }

    // Mark as executing for this config
    console.log("🔄 [DEBUG] Marking execution as started");
    hasExecutedRef.current = true;
    lastConfigRef.current = config;
    isCurrentlyExecutingRef.current = true;

    const logPrefix = `[Onboarding Data Check: ai-result, ${config.dataSourceKey}]`;
    console.log(`${logPrefix} (first 2 rows):`, (data as any[]).slice(0, 2));

    const sortedData = [...(data as any[])].sort(
      (a, b) => (b.cost || 0) - (a.cost || 0),
    );
    const dataForPrompt = sortedData.slice(0, MAX_ROWS_FOR_ONBOARDING);

    // Compress data by removing null/undefined values and truncating long strings
    const compressedData = dataForPrompt.map((row) => {
      const compressed: any = {};
      Object.entries(row).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          // Truncate very long strings to prevent oversized payloads
          if (typeof value === "string" && value.length > 200) {
            compressed[key] = value.substring(0, 200) + "...";
          } else {
            compressed[key] = value;
          }
        }
      });
      return compressed;
    });

    const dataString = JSON.stringify(compressedData, null, 2);
    const fullPrompt = `${config.promptTemplate}\n\n## Here is the data for your analysis:\n\n${dataString}`;

    console.log("📝 [DEBUG] Prompt construction complete");
    console.log(
      "📝 [DEBUG] - Prompt template length:",
      config.promptTemplate.length,
    );
    console.log("📝 [DEBUG] - Data string length:", dataString.length);
    console.log("📝 [DEBUG] - Full prompt length:", fullPrompt.length);
    console.log("📝 [DEBUG] - Data rows being sent:", dataForPrompt.length);

    // Set prompt for editing
    setEditablePrompt(fullPrompt);

    console.log(
      "[Onboarding AI] Prompt start:",
      fullPrompt.substring(0, 100) + "...",
    );
    console.log(
      "📝 [DEBUG] Full prompt first 200 chars:",
      fullPrompt.substring(0, 200),
    );
    console.log(
      "📝 [DEBUG] Data string first 200 chars:",
      dataString.substring(0, 200),
    );

    console.log("🎯 [DEBUG] About to call handleExecute");
    handleExecute(fullPrompt, onboardingData);
    console.log("🎯 [DEBUG] handleExecute call initiated");
  }, [
    config,
    onboardingDataLoadingStatus,
    onboardingData,
    isLoaded,
    isSignedIn,
    user,
    hasDataBeenFetched,
  ]);

  // Force data fetch on component mount if no data exists
  useEffect(() => {
    console.log("🔥 [FORCE FETCH] Component mounted, checking for data");
    console.log("🔥 [FORCE FETCH] - onboardingData exists:", !!onboardingData);
    console.log(
      "🔥 [FORCE FETCH] - loadingStatus:",
      onboardingDataLoadingStatus,
    );
    console.log("🔥 [FORCE FETCH] - hasDataBeenFetched:", hasDataBeenFetched);

    // Only force fetch if we haven't fetched data at all
    if (
      !hasDataBeenFetched &&
      !onboardingData &&
      onboardingDataLoadingStatus === "idle"
    ) {
      console.log(
        "🔥 [FORCE FETCH] No data found and not previously fetched, forcing fetch from fallback URL",
      );
      fetchOnboardingData(DEV_FALLBACK_WEB_APP_URL);
    }

    // If we have data but it's empty, also force refetch (only if not previously fetched)
    if (
      !hasDataBeenFetched &&
      onboardingData &&
      (!onboardingData.account || onboardingData.account.length === 0)
    ) {
      console.log(
        "🔥 [FORCE FETCH] Data exists but account is empty and not previously fetched, forcing refetch",
      );
      fetchOnboardingData(DEV_FALLBACK_WEB_APP_URL);
    }
  }, [
    onboardingData,
    onboardingDataLoadingStatus,
    fetchOnboardingData,
    hasDataBeenFetched,
  ]);

  const handleExecute = async (
    prompt: string,
    executionOnboardingData: typeof onboardingData,
  ) => {
    console.log("🎯 [DEBUG] ======= HANDLE EXECUTE CALLED =======");
    console.log("🎯 [DEBUG] Prompt length:", prompt.length);
    console.log("🎯 [DEBUG] Execution data exists:", !!executionOnboardingData);

    setIsExecuting(true);
    setExecutionError(null);
    setExecutionResult(null);

    // Account validation
    console.log("🏢 [DEBUG] Validating account information");
    const accountInfo = executionOnboardingData?.account?.[0];
    console.log("🏢 [DEBUG] Account info:", accountInfo);

    if (!accountInfo || !accountInfo.accountCID) {
      console.log("❌ [DEBUG] No account info - marking execution complete");
      isCurrentlyExecutingRef.current = false;
      setExecutionError(
        "Could not determine the Account ID from the 'account' tab in your data sheet. Please ensure it is present and correct.",
      );
      setIsExecuting(false);
      return;
    }
    const onboardingAccountId = accountInfo.accountCID;
    console.log(
      `✅ [DEBUG] Using Account ID from sheet: ${onboardingAccountId}`,
    );

    const promptId = `onboarding-${insightId}`;
    console.log("📧 [DEBUG] Constructed promptId:", promptId);
    console.log("📧 [DEBUG] Active account ID:", onboardingAccountId);

    try {
      console.log("🌐 [DEBUG] ======= STARTING BACKEND API CALL =======");
      console.log("🌐 [DEBUG] Using Gemini Flash via backend");
      console.log("🌐 [DEBUG] Timestamp:", new Date().toISOString());
      console.log(
        "🌐 [DEBUG] Prompt first 100 chars:",
        prompt.substring(0, 100) + "...",
      );

      const result = await callAI(
        [{ role: "user", content: prompt }],
        promptId,
        onboardingAccountId,
      );

      console.log("🎉 [DEBUG] Backend API call completed");
      console.log("🎉 [DEBUG] Result success:", result.success);

      if (result.success && result.content) {
        console.log(
          "🎉 [DEBUG] Response content length:",
          result.content.length,
        );
        console.log(
          "🎉 [DEBUG] Response content first 200 chars:",
          result.content.substring(0, 200),
        );

        // Convert backend response to LLMResponse format
        const responseData: LLMResponse = {
          content: result.content,
          usage: result.usage || { inputTokens: 0, outputTokens: 0 },
          modelId: "gemini-3-flash-preview",
        };

        console.log("✅ [DEBUG] About to call setExecutionResult");
        setExecutionResult(responseData);

        // Cache the result to prevent duplicate API calls on refresh
        if (onboardingAccountId && config.insightID) {
          const cacheKey = getCacheKey(config.insightID, onboardingAccountId);
          const cacheData = {
            result: responseData,
            prompt: editablePrompt,
            timestamp: Date.now(),
          };
          sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
          console.log("💾 [CACHE] Result cached for future visits");
        }

        console.log("✅ [DEBUG] setExecutionResult called");
      } else {
        const errorMessage = result.error || "Failed to get AI response";
        console.log("❌ [DEBUG] Backend API error:", errorMessage);
        setExecutionError(errorMessage);
      }
    } catch (err) {
      console.log("💥 [DEBUG] ======= BACKEND API CALL FAILED =======");
      console.log("💥 [DEBUG] Error object:", err);

      const errorString =
        err instanceof Error
          ? err.message
          : "An unknown error occurred during execution.";
      console.log("💥 [DEBUG] Error string to display:", errorString);

      console.log("❌ [DEBUG] About to call setExecutionError");
      setExecutionError(errorString);
      console.log("❌ [DEBUG] setExecutionError called");
    } finally {
      console.log("🏁 [DEBUG] ======= EXECUTION CLEANUP =======");
      console.log("🏁 [DEBUG] Setting isExecuting to false");
      console.log("🏁 [DEBUG] Marking execution complete");
      isCurrentlyExecutingRef.current = false;
      setIsExecuting(false);
      console.log("🏁 [DEBUG] Cleanup complete");
    }
  };

  const copyPromptToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(editablePrompt);
      // You could add a toast notification here
      console.log("✅ Prompt copied to clipboard");
    } catch (err) {
      console.error("❌ Failed to copy prompt:", err);
    }
  };

  const executeWithEditedPrompt = () => {
    if (editablePrompt.trim() && onboardingData) {
      console.log("🔄 [DEBUG] Executing with edited prompt");
      // Clear cache since user is manually re-executing
      const accountInfo = onboardingData?.account?.[0];
      if (accountInfo?.accountCID && config?.insightID) {
        const cacheKey = getCacheKey(config.insightID, accountInfo.accountCID);
        sessionStorage.removeItem(cacheKey);
        console.log("🗑️ [CACHE] Cache cleared due to manual re-execution");
      }
      handleExecute(editablePrompt, onboardingData);
    }
  };

  // Add manual fetch button for debugging
  const forceRefetch = () => {
    console.log("🔄 [MANUAL FETCH] User triggered manual fetch");
    fetchOnboardingData(DEV_FALLBACK_WEB_APP_URL);
  };

  // Clear cache for debugging
  const clearCache = () => {
    const accountInfo = onboardingData?.account?.[0];
    if (accountInfo?.accountCID && config?.insightID) {
      const cacheKey = getCacheKey(config.insightID, accountInfo.accountCID);
      sessionStorage.removeItem(cacheKey);
      console.log("🗑️ [DEBUG] Cache manually cleared");
      // Reset state and force re-execution
      setExecutionResult(null);
      setExecutionError(null);
      setIsExecuting(true);
      hasExecutedRef.current = false;
      lastConfigRef.current = null;
    }
  };

  const goToResults = () => {
    navigate("/onboarding/results");
  };

  if (!config) {
    return (
      <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
        <div className="relative z-10 px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <Alert
              variant="destructive"
              className="bg-gradient-card backdrop-blur-sm border border-border/50"
            >
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Invalid Insight</AlertTitle>
              <AlertDescription>
                The insight you requested is not valid.
                <Button asChild variant="link">
                  <Link to="/onboarding/tools-choice">
                    Choose a valid insight
                  </Link>
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-0 w-64 h-64 bg-success/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="relative z-10 px-6 py-16">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Navigation - Removed top navigation links */}

          {/* Header */}
          <div
            className="text-center mb-12 animate-fade-in"
            style={{ animationDelay: "100ms" }}
          >
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              AI Analysis:
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                {" "}
                {config.title}
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Here are the results based on your account data.
            </p>
          </div>

          {/* Trial Limitations Banner */}
          <div
            className="max-w-4xl mx-auto mb-8 animate-fade-in"
            style={{ animationDelay: "150ms" }}
          >
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800">
              <CardContent className="p-6">
                <div className="text-center space-y-3">
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                    🚀 Trial Analysis (Limited to {MAX_ROWS_FOR_ONBOARDING}{" "}
                    rows)
                  </h3>
                  <p className="text-blue-700 dark:text-blue-300 max-w-2xl mx-auto">
                    You're experiencing AI insights with a sample of your data
                    using Mike's Gemini account.
                    <strong> Sign up for your free 8020agent account</strong> to
                    analyze unlimited rows and access Gemini & Anthropic models.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Prompt Display Section */}
          <Collapsible
            open={isPromptOpen}
            onOpenChange={setIsPromptOpen}
            className="animate-fade-in"
            style={{ animationDelay: "200ms" }}
          >
            <Card className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between p-6 font-normal hover:bg-transparent"
                >
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    <span className="font-medium">View & Edit Prompt</span>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${isPromptOpen ? "rotate-180" : ""}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-6 pb-6 space-y-4">
                <div className="text-sm text-muted-foreground">
                  You can view, copy, and edit the prompt that will be sent to
                  the AI. Changes will only apply to this execution.
                </div>
                <Textarea
                  value={editablePrompt}
                  onChange={(e) => setEditablePrompt(e.target.value)}
                  placeholder="Prompt will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-background/50"
                  disabled={isExecuting}
                />
                <div className="flex justify-between pt-4">
                  <Button
                    onClick={executeWithEditedPrompt}
                    size="sm"
                    disabled={isExecuting || !editablePrompt.trim()}
                    className="flex items-center gap-2"
                  >
                    {isExecuting && (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    )}
                    Execute Edited Prompt
                  </Button>
                  <Button
                    onClick={copyPromptToClipboard}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Prompt
                  </Button>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Results Display */}
          <div className="animate-fade-in" style={{ animationDelay: "300ms" }}>
            <PromptResponseDisplay
              isExecuting={isExecuting}
              executionResult={executionResult}
              executionError={executionError}
              hideCost={true}
            />
          </div>

          {/* Navigation Footer - Only show after successful AI response */}
          {executionResult && !isExecuting && (
            <div
              className="border-t pt-8 mt-16 animate-fade-in"
              style={{ animationDelay: "400ms" }}
            >
              <Card className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold mb-2">
                      What would you like to do next?
                    </h3>
                    <p className="text-muted-foreground">
                      Choose your next step to continue exploring your account
                      insights.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Button
                      onClick={() => navigate("/onboarding/ai-run")}
                      variant="default"
                      size="lg"
                      className="w-full sm:w-auto"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Try Different AI Analysis
                    </Button>

                    <Button
                      onClick={() => navigate("/onboarding/results")}
                      variant="outline"
                      size="lg"
                      className="w-full sm:w-auto"
                    >
                      <BarChart2 className="w-4 h-4 mr-2" />
                      Back to Results Page
                    </Button>

                    <Button
                      onClick={() => navigate("/onboarding/benefits")}
                      variant="outline"
                      size="lg"
                      className="w-full sm:w-auto"
                    >
                      <ChevronsRight className="w-4 h-4 mr-2" />
                      Explore Full 8020 Agent
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Debug Panel */}
      {DEBUG && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
          <Card className="bg-gradient-card backdrop-blur-sm border border-border/50 shadow-large">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Bug className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Debug Panel</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isDebugMode}
                    onCheckedChange={(checked) => {
                      setIsDebugMode(checked);
                      localStorage.setItem(
                        "onboarding-debug-mode",
                        checked.toString(),
                      );
                    }}
                    id="debug-mode"
                  />
                  <label htmlFor="debug-mode" className="text-sm">
                    Debug Mode
                  </label>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={forceRefetch}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Force Refetch Data
                  </Button>
                  <Button
                    onClick={clearCache}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    🗑️ Clear Cache
                  </Button>
                  <Button
                    onClick={goToResults}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    Go to Results
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default OnboardingAiResultPage;
